import type {
  HotelResultsFiltersCopy,
  HotelResultsFilterValue,
  HotelResultsPageCopy,
  ResultsOfflineRequestProps,
} from '@voll-intelligence/intelligence-ui-kit';
import type { GenUiOfflineRequestPort } from '../../../shared/ui/config.js';
import type { GenUiTranslate } from '@voll-intelligence/genui-contract';
import { offlineRequestCopy } from '../../../shared/ui/offline_request_copy.js';

/**
 * O PERFIL que a busca offline de hotel envia à equipe VOLL — os chips do
 * banner (`ResultsOfflineRequest`) e, tal-qual, o que o host anexa ao pedido.
 * Lugar + datas + ocupação + cada corte ativo, na ordem do painel de filtros e
 * com os MESMOS rótulos dele (o que a pessoa marcou é o que ela lê no chip).
 * Faixa de nota vira o piso mais baixo marcado ("4,0+"): duas faixas são "uma
 * OU outra", e o piso menor é a leitura honesta do corte. Comodidade sem
 * rótulo no catálogo em cena fica de fora — um id cru não é perfil.
 */
export function hotelOfflineProfile(input: {
  readonly place: string | undefined;
  readonly dates: string | undefined;
  readonly occupancy: string | undefined;
  readonly filters: HotelResultsFilterValue;
  readonly filtersCopy: HotelResultsFiltersCopy;
  readonly chipsCopy: Pick<
    HotelResultsPageCopy['chips'],
    'priceUpTo' | 'priceFrom' | 'priceBetween'
  >;
  readonly amenityLabel: (id: string) => string | undefined;
}): string[] {
  const { filters, filtersCopy, chipsCopy } = input;
  const chips: (string | undefined)[] = [input.place, input.dates, input.occupancy];
  if (filters.policyOnly) chips.push(filtersCopy.withinPolicyLabel);
  if (filters.breakfastOnly) chips.push(filtersCopy.breakfastLabel);
  if (filters.refundableOnly) chips.push(filtersCopy.refundableLabel);
  if (filters.agreedRateOnly === true) chips.push(filtersCopy.agreedRateLabel);
  const { minimumNightlyPrice: min, maximumNightlyPrice: max } = filters;
  if (min !== undefined && max !== undefined) chips.push(chipsCopy.priceBetween(min, max));
  else if (max !== undefined) chips.push(chipsCopy.priceUpTo(max));
  else if (min !== undefined) chips.push(chipsCopy.priceFrom(min));
  const bands = filters.ratingBands ?? [];
  if (bands.length > 0) chips.push(filtersCopy.ratingThresholdLabel(Math.min(...bands)));
  if (filters.maximumDistance !== undefined) {
    chips.push(filtersCopy.distanceUpToLabel(filters.maximumDistance));
  }
  chips.push(...filters.amenityIds.map(input.amenityLabel));
  return chips.filter((chip): chip is string => chip !== undefined && chip.trim() !== '');
}

/** A prop `offlineRequest` da página de hotel a partir do port do host — só com
 *  o port (a casca do chat não o passa, e a página termina na sentinela). */
export function hotelOfflineRequestProp(
  port: GenUiOfflineRequestPort | undefined,
  t: GenUiTranslate,
  profile: readonly string[],
): { offlineRequest?: ResultsOfflineRequestProps } {
  if (!port) return {};
  return {
    offlineRequest: {
      copy: offlineRequestCopy(t, 'hotel'),
      onRequest: () => {
        port.onRequest(profile);
      },
    },
  };
}
